/**
 * Mappe une erreur d'upload (telle qu'émise par l'intercepteur axios
 * `client.ts`) vers un `MappedError` consommé par le provider d'upload.
 *
 * Générique — utilisable pour tout consommateur de `useBulkUploadStack`
 * qui souhaite distinguer 409 `duplicate_content` / `duplicate_slug` du
 * BE des autres erreurs. Les uploaders qui n'ont pas de notion de slug
 * (ex: œuvres) peuvent simplement ne pas passer de `mapError` et se
 * contenter du default qui mappe tout en `error`.
 *
 * IMPORTANT : axios est intercepté par `client.ts` qui transforme toute
 * erreur réseau en `ApiError = {detail, status, code?}`. On ne reçoit
 * jamais l'`AxiosError` natif ici — d'où la consommation par shape de
 * l'objet plutôt qu'`isAxiosError`. Si quelqu'un casse `client.ts`
 * un jour, le fallback `'error'` + message générique évite un crash.
 *
 * Le BE expose un `detail` structuré sur 409 :
 * `{ code: 'duplicate_content' | 'duplicate_slug', message: '...' }`.
 * Le transformer côté `client.ts` aplatit ça en `{detail: message,
 * code: 'duplicate_content'}`.
 */

import type { ApiError } from '@/utils/api/client'
import type { MappedError } from '@/contexts/bulk-upload-stack-context'

export function _mapErrorToStatus(error: unknown, file: File): MappedError {
  if (!error || typeof error !== 'object') {
    return { status: 'error', message: String(error) }
  }

  const apiErr = error as Partial<ApiError>
  const status = apiErr.status
  const code = apiErr.code
  const detail = typeof apiErr.detail === 'string' ? apiErr.detail : 'Erreur inconnue'

  if (status === 409 && code === 'duplicate_content') {
    return { status: 'duplicate_content', message: detail }
  }
  if (status === 409 && code === 'duplicate_slug') {
    return {
      status: 'duplicate_slug',
      message: `Le nom dérivé de « ${file.name} » est déjà utilisé. Renomme le fichier sur ton disque puis re-drag.`,
    }
  }
  return { status: 'error', message: detail }
}
